import React, { useState, useEffect, useRef } from "react";
import { Icon, Switch } from "../../";
import { useUpdateDriverGroup } from "../../../../hooks/useDriverCompanies";
import {
  secondsToTime,
  timeToSeconds,
  validateTimeInput,
  parseTimeInputs,
  ORDER_STATUS_OPTIONS,
  PRIORITY_OPTIONS,
} from "./utils";

const DriverGroupAssignmentSettings = ({ driverGroup, driverGroupId }) => {
  const [formData, setFormData] = useState({
    orders_group_start_in_status: 7,
    autoassign_autoassign_driver: false,
    autoreject_after_expiration: false,
    priority: 0,
  });

  const [timeInputs, setTimeInputs] = useState({
    hours: 0,
    minutes: 0,
    seconds: 30,
  });

  const updateMutation = useUpdateDriverGroup();
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (driverGroup) {
      setFormData({
        orders_group_start_in_status:
          driverGroup.orders_group_start_in_status ?? 7,
        autoassign_autoassign_driver:
          driverGroup.autoassign_autoassign_driver ?? false,
        autoreject_after_expiration:
          driverGroup.autoreject_after_expiration ?? false,
        priority: driverGroup.priority ?? 0,
      });

      // Convert autoreject time from seconds to hours/minutes/seconds
      const autorejectTime = driverGroup.autoassign_autoreject_time ?? 0;
      const time = secondsToTime(autorejectTime);
      setTimeInputs(time);
    }
  }, [driverGroup]);

  const handleChange = async (field, value) => {
    // Update local state optimistically
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Save immediately
    try {
      await updateMutation.mutateAsync({
        driverGroupId,
        data: {
          [field]: value,
        },
      });
    } catch (error) {
      // Revert on error
      if (driverGroup) {
        setFormData((prev) => ({
          ...prev,
          [field]: driverGroup[field],
        }));
      }
      console.error("Error updating driver group:", error);
    }
  };

  const handleTimeInputChange = (unit, value) => {
    const validation = validateTimeInput(value, unit);

    if (!validation.isValid) return;

    if (validation.isEmpty) {
      setTimeInputs((prev) => ({ ...prev, [unit]: "" }));
      return;
    }

    const updatedTimeInputs = {
      ...timeInputs,
      [unit]: validation.numValue,
    };

    setTimeInputs(updatedTimeInputs);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      const { hours, minutes, seconds } = parseTimeInputs(updatedTimeInputs);

      const totalSeconds = timeToSeconds(hours, minutes, seconds);

      try {
        await updateMutation.mutateAsync({
          driverGroupId,
          data: {
            autoassign_autoreject_time: totalSeconds,
          },
        });
      } catch {
        if (driverGroup) {
          const time = secondsToTime(
            driverGroup.autoassign_autoreject_time ?? 0,
          );
          setTimeInputs(time);
        }
      }
    }, 1000);
  };

  const handleTimeInputBlur = (unit) => {
    if (timeInputs[unit] === "") {
      setTimeInputs((prev) => ({ ...prev, [unit]: 0 }));
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
      <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
        <Icon name="Clock" className="mr-2 h-5 w-5 text-gray-400" />
        When would you like this automatic assignment process to begin
      </h3>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Order Status
          </label>
          <select
            value={formData.orders_group_start_in_status}
            onChange={(e) =>
              handleChange(
                "orders_group_start_in_status",
                parseInt(e.target.value),
              )
            }
            disabled={updateMutation.isPending}
            className="custom-select w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {ORDER_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Assign Automatically Toggle */}
        <div className="flex items-center justify-between rounded-lg bg-gray-800/50 p-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300">
              Assign Automatically
            </label>
            <p className="mt-1 text-xs text-gray-400">
              Automatically assign drivers to orders when they reach the
              selected status
            </p>
          </div>
          <Switch
            checked={formData.autoassign_autoassign_driver}
            onCheckedChange={(checked) =>
              handleChange("autoassign_autoassign_driver", checked)
            }
            disabled={updateMutation.isPending}
          />
        </div>

        {/* Auto-reject After Expiration Toggle */}
        <div className="flex items-center justify-between rounded-lg bg-gray-800/50 p-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300">
              Auto-reject After Expiration
            </label>
            <p className="mt-1 text-xs text-gray-400">
              Automatically reject orders after the expiration time
            </p>
          </div>
          <Switch
            checked={formData.autoreject_after_expiration}
            onCheckedChange={(checked) =>
              handleChange("autoreject_after_expiration", checked)
            }
            disabled={updateMutation.isPending}
          />
        </div>

        {/* Auto-reject Time Inputs */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Auto reject Order if not assign in
          </label>
          <div className="flex items-center space-x-2">
            {/* Hours Input */}
            <div className="flex-1">
              <input
                type="text"
                value={timeInputs.hours}
                onChange={(e) => handleTimeInputChange("hours", e.target.value)}
                onBlur={() => handleTimeInputBlur("hours")}
                disabled={updateMutation.isPending}
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-center text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="0"
              />
              <p className="mt-1 text-center text-xs text-gray-400">Hours</p>
            </div>

            <span className="pb-5 text-xl text-gray-400">:</span>

            {/* Minutes Input */}
            <div className="flex-1">
              <input
                type="text"
                value={timeInputs.minutes}
                onChange={(e) =>
                  handleTimeInputChange("minutes", e.target.value)
                }
                onBlur={() => handleTimeInputBlur("minutes")}
                disabled={updateMutation.isPending}
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-center text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="0"
              />
              <p className="mt-1 text-center text-xs text-gray-400">Minutes</p>
            </div>

            <span className="pb-5 text-xl text-gray-400">:</span>

            {/* Seconds Input */}
            <div className="flex-1">
              <input
                type="text"
                value={timeInputs.seconds}
                onChange={(e) =>
                  handleTimeInputChange("seconds", e.target.value)
                }
                onBlur={() => handleTimeInputBlur("seconds")}
                disabled={updateMutation.isPending}
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-center text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="0"
              />
              <p className="mt-1 text-center text-xs text-gray-400">Seconds</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Time before automatically rejecting unassigned orders
          </p>
        </div>

        {/* Priority Select */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => handleChange("priority", parseInt(e.target.value))}
            disabled={updateMutation.isPending}
            className="custom-select w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-400">
            Set the priority level for this delivery service group
          </p>
        </div>
      </div>
    </div>
  );
};

export default DriverGroupAssignmentSettings;
